import React, { useState, useEffect } from 'react';
import './divulgacaoCientifica.css';  
import CardsItemArtigos from '../../components/DivulgacaoCientificaComponents/CardsItemArtigos';
import CardsItemPodcast from '../../components/DivulgacaoCientificaComponents/Podcast/CardsItemPodcast'; // Importando o componente de podcast
import { motion } from 'framer-motion';
import { TextField,   Pagination, CircularProgress, Container, Typography, Box, Button } from '@mui/material';
 

const ITEMS_PER_PAGE = 6;
const PODCAST_SERIES = {
  CALILA_DIMNA: {
    key: 'calila-dimna',
    title: 'O papel moral das fábulas na Castela do século XIII: as histórias de Calila e Dimna (Comunicar Ciência)',
    rank: 2,
    keywords: ['berzebuey', 'gato e o rato', 'calila e dimna', 'fabula e a moralidade', 'fábula e a moralidade'],
  },
  TRAJETORIAS: {
    key: 'trajetorias-pesquisa',
    title: 'História da Península e Trajetórias de Pesquisa: um podcast sobre o mundo ibérico medieval e moderno',
    rank: 1,
    keywords: ['exemplum', 'pecados da lingua', 'pecados da língua', 'modos de vestir', 'espelhos de principes', 'espelhos de príncipes', 'antijudaica', 'horto do esposo', 'historiografia iberica', 'historiografia ibérica', 'tempos de peste'],
  },
};

const PODCAST_TEXT_ORDER = new Map([
  ['primeiro', 1],
  ['segundo', 2],
  ['terceiro', 3],
  ['quarto', 4],
  ['quinto', 5],
  ['sexto', 6],
  ['setimo', 7],
  ['sétimo', 7],
  ['oitavo', 8],
  ['nono', 9],
  ['decimo', 10],
  ['décimo', 10],
  ['decimo primeiro', 11],
  ['décimo primeiro', 11],
  ['decimo segundo', 12],
  ['décimo segundo', 12],
  ['decimo terceiro', 13],
  ['décimo terceiro', 13],
  ['decimo quarto', 14],
  ['décimo quarto', 14],
  ['decimo quinto', 15],
  ['décimo quinto', 15],
]);

const normalizePodcastTitle = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const getPodcastSeries = (podcast) => {
  const explicitSeries = String(podcast?.serie || '').trim();
  if (explicitSeries === PODCAST_SERIES.CALILA_DIMNA.key) return PODCAST_SERIES.CALILA_DIMNA;
  if (explicitSeries === PODCAST_SERIES.TRAJETORIAS.key) return PODCAST_SERIES.TRAJETORIAS;

  const text = normalizePodcastTitle(`${podcast?.titulo || ''} ${podcast?.descricao || ''}`);
  if (PODCAST_SERIES.CALILA_DIMNA.keywords.some((word) => text.includes(word))) {
    return PODCAST_SERIES.CALILA_DIMNA;
  }
  return PODCAST_SERIES.TRAJETORIAS;
};

const extractPodcastOrder = (title = '') => {
  const normalized = normalizePodcastTitle(title);
  const numericMatch = normalized.match(/\bepisodio\s*(\d+)\b|\bep\.?\s*(\d+)\b|\b(\d+)\b/);
  const numericOrder = Number(numericMatch?.[1] || numericMatch?.[2] || numericMatch?.[3]);
  if (Number.isFinite(numericOrder)) return numericOrder;

  for (const [word, order] of PODCAST_TEXT_ORDER.entries()) {
    if (normalized.includes(word)) return order;
  }

  return Number.MAX_SAFE_INTEGER;
};

const sortPodcastsByTitleOrder = (items = []) =>
  [...items].sort((a, b) => {
    const seriesA = getPodcastSeries(a);
    const seriesB = getPodcastSeries(b);
    if (seriesA.rank !== seriesB.rank) return seriesA.rank - seriesB.rank;

    const orderA = extractPodcastOrder(a?.titulo);
    const orderB = extractPodcastOrder(b?.titulo);
    if (orderA !== orderB) return orderA - orderB;

    return String(a?.titulo || '').localeCompare(String(b?.titulo || ''), 'pt-BR', {
      sensitivity: 'base',
    });
  });

const groupPodcastsBySeries = (items = []) => {
  const grouped = {
    [PODCAST_SERIES.CALILA_DIMNA.key]: [],
    [PODCAST_SERIES.TRAJETORIAS.key]: [],
  };

  items.forEach((podcast) => {
    const series = getPodcastSeries(podcast);
    grouped[series.key].push(podcast);
  });

  return grouped;
};

const sectionTitleStyle = {
  fontFamily: "'Source Sans Pro', sans-serif",
  fontWeight: 700,
  color: "#f9f8f6",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: 0,
  padding: "0 0 8px 0",
  marginTop: "40px",
  textTransform: "uppercase",
  textShadow: "0 2px 6px rgba(0,0,0,0.55)",
  borderBottom: "3px solid #d4af37",
  display: "inline-block",
};

const DivulgacaoCientificaComponent = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "/api";

  const [cardItems, setCardItems] = useState([]);
  const [podcastItems, setPodcastItems] = useState([]); // Estado para podcasts
  const [filteredCardItems, setFilteredCardItems] = useState([]);
  const [filteredPodcastItems, setFilteredPodcastItems] = useState([]); // Estado para filtragem de podcasts
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQueryArticles, setSearchQueryArticles] = useState('');
  const [searchQueryPodcasts, setSearchQueryPodcasts] = useState('');
  const [view, setView] = useState('artigos'); // Estado para alternar entre artigos e podcasts
  

const [currentPageArticles, setCurrentPageArticles] = useState(1); // Paginação para Artigos
const indexOfLastItemArticles = currentPageArticles * ITEMS_PER_PAGE;
const indexOfFirstItemArticles = indexOfLastItemArticles - ITEMS_PER_PAGE;
const currentItemsArticles = filteredCardItems.slice(indexOfFirstItemArticles, indexOfLastItemArticles);

const currentItemsPodcasts = filteredPodcastItems;

const totalPagesArticles = Math.max(1, Math.ceil(filteredCardItems.length / ITEMS_PER_PAGE));
const groupedCurrentPodcasts = groupPodcastsBySeries(currentItemsPodcasts);

const handlePageChange = (event, value) => {
  if (view === 'artigos') setCurrentPageArticles(value);
};

useEffect(() => {
  setCurrentPageArticles((prev) => Math.min(prev, totalPagesArticles));
}, [totalPagesArticles]);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const [artigosResponse, podcastsResponse] = await Promise.all([
          fetch(`${apiUrl}/artigos`),
          fetch(`${apiUrl}/podcasts`),
        ]);

        if (!artigosResponse.ok || !podcastsResponse.ok) throw new Error('Erro ao buscar dados da API');
        
        const artigosData = await artigosResponse.json();
        const podcastsData = await podcastsResponse.json();

        setCardItems(artigosData);
        const sortedPodcasts = sortPodcastsByTitleOrder(podcastsData);
        setPodcastItems(sortedPodcasts);
        setFilteredCardItems(artigosData);
        setFilteredPodcastItems(sortedPodcasts);
        setIsLoading(false);
      } catch (error) {
        setError(error.message);
        setIsLoading(false);
      }
    };

    fetchCards();
  }, [apiUrl]);

  const handleSearchArticles = (e) => {
    const str = e.target.value;
    setSearchQueryArticles(str);
    const filtered = cardItems.filter((item) =>
      item.titulo.toLowerCase().includes(str.toLowerCase()) ||
      item.conteudo.toLowerCase().includes(str.toLowerCase())
    );
    setFilteredCardItems(filtered);
    setCurrentPageArticles(1);
  };

  const handleSearchPodcasts = (e) => {
    const str = e.target.value || ""; // Garante que str tenha uma string vazia se for undefined ou null
    setSearchQueryPodcasts(str);
  
    const filtered = podcastItems.filter((item) =>
      (item.titulo && item.titulo.toLowerCase().includes(str.toLowerCase())) || // Verifica se item.titulo não é undefined ou null
      (item.descricao && item.descricao.toLowerCase().includes(str.toLowerCase()))
    );
  
    setFilteredPodcastItems(sortPodcastsByTitleOrder(filtered));
  };

  if (isLoading) {
    return <CircularProgress className="loading-spinner" />;
  }

  if (error) {
    return <Typography color="error">Erro: {error}</Typography>;
  }


  return (
    <motion.div 
      className="divulgacao-cientifica-background"
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ duration: 1 }}
    >   
     
     <section className="divulgacao-cientifica-secion">
     <Container maxWidth="lg">
  <Typography
    variant="h2"
    align="center"
    gutterBottom
    style={{
      fontFamily: "'Source Sans Pro', sans-serif",
      fontWeight: 700,
      color: "#f9f8f6",
      borderBottom: "4px solid #d4af37",
      paddingBottom: "10px",
      textTransform: "uppercase",
      animation: "glow 2s infinite alternate",
      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
      marginTop: "7%"  // Aqui é onde você define o espaço superior
    }}
  >
          Divulgação Científica
        </Typography>

  <Box display="flex" justifyContent="center" mb={4}>
  <TextField
    label={view === 'artigos' ? 'Buscar Matérias' : 'Buscar Podcasts'}
    variant="outlined"
    fullWidth
    value={view === 'artigos' ? searchQueryArticles : searchQueryPodcasts}
    onChange={view === 'artigos' ? handleSearchArticles : handleSearchPodcasts}
    sx={{
      maxWidth: 600,
      backgroundColor: '#fff', // Fundo branco para destacar
      borderRadius: '8px', // Bordas arredondadas
      '& .MuiOutlinedInput-root': {
        borderColor: '#FFD700', // Cor da borda
        '&:hover fieldset': {
          borderColor: '#FFA500', // Cor ao passar o mouse
        },
        '&.Mui-focused fieldset': {
          borderColor: '#FFD700', // Cor quando o campo é focado
        },
      },
      '& .MuiInputLabel-root': {
        color: '#555', // Cor do texto do label
      },
      '& .MuiInputBase-input': {
        color: '#333', // Cor do texto dentro do campo
      },
    }}
  />
</Box>

        {/* Botões para alternar entre artigos e podcasts */}
        <Box display="flex" justifyContent="center" mb={4}>
          <Button
            variant={view === 'artigos' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setView('artigos')}
            sx={{
              marginRight: '10px',
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: view === 'artigos' ? 'bold' : 'normal',
              borderRadius: '20px',
              transition: 'all 0.3s ease',
              backgroundColor: view === 'artigos' ? '#FFD700' : 'transparent',
              '&:hover': {
                backgroundColor: '#f5a623',
              },
            }}
          >
            Matérias
          </Button>
          <Button
            variant={view === 'podcasts' ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setView('podcasts')}
            sx={{
              padding: '10px 20px',
              fontSize: '16px',
              fontWeight: view === 'podcasts' ? 'bold' : 'normal',
              borderRadius: '20px',
              transition: 'all 0.3s ease',
              backgroundColor: view === 'podcasts' ? '#FFD700' : 'transparent',
              '&:hover': {
                backgroundColor: '#f5a623',
              },
            }}
          >
            Podcasts
          </Button>
        </Box>

        {/* Transição suave com motion.div */}
        <motion.div 
          key={view} 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ duration: 0.5 }}
        >
          {/* Exibição de Artigos ou Podcasts dependendo do estado 'view' */}
          {view === 'artigos' ? (
            <>
              <Typography
                variant="h4"
                align="center"
                gutterBottom
                style={sectionTitleStyle}
              >
                Matérias 
              </Typography>
              <div className="grid-artigos">
                {currentItemsArticles.map((card) => (
                  <CardsItemArtigos
                    key={card._id}
                    src={card.imagens[0]}
                    name={card.titulo}
                    subtitle={card.conteudo.substring(0, 100)}
                    path={`/artigo/${card._id}`} 
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <Typography
                variant="h4"
                align="center"
                gutterBottom
                style={sectionTitleStyle}
              >
                Podcasts
              </Typography>
              <div className="podcast-sections">
                {groupedCurrentPodcasts[PODCAST_SERIES.CALILA_DIMNA.key].length > 0 && (
                  <Box className="podcast-section-card">
                    <Typography className="podcast-section-title" variant="h6">
                      {PODCAST_SERIES.CALILA_DIMNA.title}
                    </Typography>
                    <Box display="flex" justifyContent="center" flexWrap="wrap">
                      {groupedCurrentPodcasts[PODCAST_SERIES.CALILA_DIMNA.key].map((podcast) => (
                        <Box key={podcast._id} m={2}>
                          <CardsItemPodcast podcast={podcast} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {groupedCurrentPodcasts[PODCAST_SERIES.TRAJETORIAS.key].length > 0 && (
                  <Box className="podcast-section-card">
                    <Typography className="podcast-section-title" variant="h6">
                      {PODCAST_SERIES.TRAJETORIAS.title}
                    </Typography>
                    <Box display="flex" justifyContent="center" flexWrap="wrap">
                      {groupedCurrentPodcasts[PODCAST_SERIES.TRAJETORIAS.key].map((podcast) => (
                        <Box key={podcast._id} m={2}>
                          <CardsItemPodcast podcast={podcast} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </div>
            </>
          )}
        </motion.div>

       
       
        {/* Paginação */}
{view === 'artigos' && (
<Box display="flex" justifyContent="center" mt={4}>
  <Pagination
  count={totalPagesArticles}
  page={currentPageArticles}
  onChange={handlePageChange}
    color="primary"
    sx={{
      '& .MuiPaginationItem-root': {
        fontSize: '1.2rem', // Aumenta o tamanho da fonte dos botões
        fontWeight: 'bold', // Torna o texto mais forte
        backgroundColor: '#2c2c2c', // Fundo escuro para os botões
        color: '#fff', // Cor do texto dos botões
        borderRadius: '8px', // Bordas arredondadas para os botões
        margin: '0 5px', // Espaçamento entre os botões
        '&:hover': {
          backgroundColor: '#D8C9A3', // Cor de fundo quando o mouse passa por cima
          color: '#000', // Cor do texto quando hover
        },
        '&.Mui-selected': {
          backgroundColor: '#FFD700', // Cor de fundo do botão ativo
          color: '#000', // Cor do texto do botão ativo
        },
      },
    }}
  />
</Box>
)}

      </Container>
      </section>
    </motion.div>
  );
};

export default DivulgacaoCientificaComponent;



 
